import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, apiRequest } from '../apiClient';
import { buildUiHelpers } from './sharedUi';
import { formatDate, formatMoney } from './sharedFormatters';

const DEFAULT_FORM = {
  market: 'SP500',
  period: '1Y',
  start_date: '',
  end_date: '',
  positions_count: 5,
  sector_positions_count: 3,
  amount_per_position: 1000,
  fee_per_trade: 0,
  max_per_industry: 0,
  classic_exit_rank_pct: 25,
};
const BACKTEST_DEFAULT_MIN_START_DATE = '2020-01-01';
const BACKTEST_SP500_1996_MIN_START_DATE = '1996-01-01';
const BACKTEST_NORGATE_MIN_START_DATE = '1990-01-01';
const HDAX_MIN_HISTORY_DATE = '2024-03-04';
const BACKTEST_MARKET_MIN_START_DATES = {
  SP500: BACKTEST_DEFAULT_MIN_START_DATE,
  NASDAQ100: BACKTEST_DEFAULT_MIN_START_DATE,
  STOXX600: BACKTEST_DEFAULT_MIN_START_DATE,
  HDAX: HDAX_MIN_HISTORY_DATE,
};
const BACKTEST_MODE_OPTIONS = [
  { value: 'next_daily_open', label: 'v0.2 Tages-Open' },
  { value: 'historical_strategy_comparison_v09', label: 'v0.9 1990 - 18.05.2026' },
  { value: 'historical_strategy_comparison_v10', label: 'v1.0 1990 - gestern' },
  { value: 'historical_admin_signal_v11', label: 'v1.1 Scanner-Strategie' },
  { value: 'parameter_comparison', label: 'Parameter-Vergleich' },
];
const DEFAULT_PERIOD_OPTIONS = [
  ['YTD', 'YTD'],
  ['1Y', '1 Jahr'],
  ['2Y', '2 Jahre'],
  ['3Y', '3 Jahre'],
  ['4Y', '4 Jahre'],
  ['5Y', '5 Jahre'],
];
const HISTORICAL_PERIOD_OPTIONS = [
  ...DEFAULT_PERIOD_OPTIONS,
  ['10Y', '10 Jahre'],
  ['15Y', '15 Jahre'],
  ['20Y', '20 Jahre'],
  ['25Y', '25 Jahre'],
  ['30Y', '30 Jahre'],
  ['CUSTOM', 'Benutzerdefiniert'],
];
const STRATEGY_COMPARISON_VARIANTS = [
  { code: 'S1', label: 'RSL Stream', universe: 'stocks' },
  { code: 'K1', label: 'Klassisch', universe: 'stocks' },
  { code: 'K2', label: 'Klassisch + MA 5/13', universe: 'stocks' },
  { code: 'K3', label: 'Klassisch + Cash', universe: 'stocks' },
  { code: 'K4', label: 'Klassisch + MA + Cash', universe: 'stocks' },
  { code: 'D1', label: 'Dynamisch', universe: 'stocks' },
  { code: 'D2', label: 'Dynamisch + MA 5/13', universe: 'stocks' },
  { code: 'D3', label: 'Dynamisch + Cash', universe: 'stocks' },
  { code: 'D4', label: 'Dynamisch + MA + Cash', universe: 'stocks' },
];
const SECTOR_STRATEGY_COMPARISON_VARIANTS = STRATEGY_COMPARISON_VARIANTS.map((variant) => ({
  ...variant,
  code: `E${variant.code}`,
  label: `Sektor ETF ${variant.label}`,
  universe: 'sector_etfs',
}));
const HISTORICAL_STRATEGY_COMPARISON_VARIANTS = [
  ...STRATEGY_COMPARISON_VARIANTS,
  ...SECTOR_STRATEGY_COMPARISON_VARIANTS,
];
const CLASSIC_EXIT_RANK_PERCENT_OPTIONS = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70];
const DEFAULT_STRATEGY_COMPARISON_CODES = STRATEGY_COMPARISON_VARIANTS.map((variant) => variant.code);
const DEFAULT_HISTORICAL_STRATEGY_COMPARISON_CODES = HISTORICAL_STRATEGY_COMPARISON_VARIANTS.map((variant) => variant.code);
const DEFAULT_SELECTABLE_BACKTEST_MODE = BACKTEST_MODE_OPTIONS[0].value;
const PARAMETER_COMPARISON_POSITIONS = Array.from({ length: 10 }, (_, index) => index + 1);
const PARAMETER_COMPARISON_EXIT_PERCENTS = Array.from({ length: 21 }, (_, index) => index + 15);
const PARAMETER_COMPARISON_EXIT_CRITERIA = [
  { value: 'index_rsl', label: 'Index-RSL' },
  { value: 'k_exit', label: 'Top % im Index' },
];
const PARAMETER_COMPARISON_MODEL_PRESETS = {
  SP500: { positions: [4, 5], exitCriteria: ['k_exit'], exitPercents: [25] },
  NASDAQ100: { positions: [4], exitCriteria: ['k_exit'], exitPercents: [22] },
  HDAX: { positions: [5, 6, 7], exitCriteria: ['index_rsl'], exitPercents: [] },
  STOXX600: { positions: [4, 5], exitCriteria: ['index_rsl'], exitPercents: [] },
};
const PARAMETER_COMPARISON_TOOLTIPS = {
  positions: 'Legt fest, wie viele Titel die Strategie gleichzeitig im Depot hält. Mehr Positionen verteilen das Risiko breiter, weniger Positionen konzentrieren stärker auf die bestplatzierten RSL-Werte.',
  exitCriteria: 'Bestimmt, nach welcher Regel eine Position verkauft wird. Index-RSL verkauft, wenn die Aktie schwächer als der übergeordnete Index wird. Top % im Index verkauft, wenn die Aktie aus dem gewählten oberen Rangbereich herausfällt.',
  exitPercents: 'Legt den Rangbereich für den K-Ausstieg fest. Beispiel: 25 % bedeutet, dass eine Aktie gehalten wird, solange sie zu den oberen 25 % ihres Index gehört. Fällt sie darunter, entsteht ein Verkaufssignal.',
};
const normalizeSelectableBacktestMode = (value) => (
  BACKTEST_MODE_OPTIONS.some((option) => option.value === value)
    ? value
    : DEFAULT_SELECTABLE_BACKTEST_MODE
);
const MARKET_LABELS = {
  SP500: 'S&P 500',
  HDAX: 'HDAX',
  NASDAQ100: 'Nasdaq 100',
  RUSSELL2000: 'Russell 2000',
  DJI: 'Dow Jones',
  STOXX600: 'STOXX Europe 600',
};
const BACKTEST_V11_DATA_START = {
  SP500: { label: 'S&P 500', date: '1990' },
  HDAX: { label: 'HDAX', date: '2024' },
  NASDAQ100: { label: 'Nasdaq 100', date: '1990' },
  STOXX600: { label: 'STOXX Europe 600', date: '2020' },
};
const BACKTEST_REQUEST_TIMEOUT_MS = 180000;
const EXTENDED_BACKTEST_REQUEST_TIMEOUT_MS = 600000;
const STRATEGY_COMPARISON_REQUEST_TIMEOUT_MS = 1200000;
const EMPTY_SERIES = [];

function formatPercent(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${formatMoney(value)}%`;
}

function formatNumber(value, fractionDigits = 0) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Number(value));
}

function formatPercentRounded(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${formatNumber(value, 0)}%`;
}

function formatParameterMoney(value, currency) {
  return currency === 'USD' ? formatNumber(value, 0) : formatMoney(value);
}

function calculateCurveStats(curve) {
  const points = (Array.isArray(curve) ? curve : [])
    .map((point) => ({
      date: point.date,
      value: Number(point.value ?? point.equity),
    }))
    .filter((point) => point.date && Number.isFinite(point.value) && point.value > 0);
  if (points.length < 2) return null;

  const start = points[0];
  const end = points[points.length - 1];
  let peak = start.value;
  let maxDrawdown = 0;
  points.forEach((point) => {
    peak = Math.max(peak, point.value);
    if (peak > 0) {
      maxDrawdown = Math.min(maxDrawdown, ((point.value / peak) - 1) * 100);
    }
  });

  const startTime = Date.parse(start.date);
  const endTime = Date.parse(end.date);
  const years = Number.isFinite(startTime) && Number.isFinite(endTime)
    ? Math.max((endTime - startTime) / (365.25 * 24 * 60 * 60 * 1000), 0)
    : 0;
  const totalPercent = ((end.value / start.value) - 1) * 100;
  const ror = years > 0 ? ((end.value / start.value) ** (1 / years) - 1) * 100 : null;
  const mar = ror !== null && maxDrawdown < 0 ? ror / Math.abs(maxDrawdown) : null;

  return {
    start_value: start.value,
    end_value: end.value,
    total_pl_percent: totalPercent,
    ror,
    max_drawdown: maxDrawdown,
    mar,
  };
}

function parameterComparisonDisplayCode(code) {
  return String(code || '')
    .replace(/^P(\d+)-K(\d+)$/, 'P$1-T$2');
}

function parameterComparisonVariantLabel(variant) {
  if (variant?.exit_criterion === 'k_exit') {
    return `${variant.positions_count} Pos · Top ${variant.exit_percent_filter}% im Index`;
  }
  return variant?.label || '';
}

function signedColor(value, COLORS) {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return COLORS.muted;
  return number > 0 ? COLORS.green : COLORS.red;
}

function EquityComparisonChart({ equityCurve, indexCurve, benchmark, COLORS, currency, themeMode = 'dark', scaleMode = 'linear' }) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const isLogScale = scaleMode === 'log';
  const width = 920;
  const height = 280;
  const leftPad = 58;
  const rightPad = 22;
  const topPad = 20;
  const bottomPad = 42;
  const indexByDate = new Map((indexCurve || []).map((point) => [point.date, point]));
  const rows = (equityCurve || [])
    .map((point) => ({
      date: point.date,
      equity: Number(point.equity),
      index: Number(indexByDate.get(point.date)?.value),
    }))
    .filter((point) => Number.isFinite(point.equity) && Number.isFinite(point.index));

  if (rows.length < 2) {
    return <div style={{ color: COLORS.muted, fontSize: '13px' }}>Noch zu wenige Datenpunkte für einen Chart.</div>;
  }

  const scaledRows = rows
    .filter((point) => !isLogScale || (point.equity > 0 && point.index > 0))
    .map((point) => ({
      ...point,
      plotEquity: isLogScale ? Math.log10(point.equity) : point.equity,
      plotIndex: isLogScale ? Math.log10(point.index) : point.index,
    }));

  if (scaledRows.length < 2) {
    return <div style={{ color: COLORS.muted, fontSize: '13px' }}>Noch zu wenige Datenpunkte für einen Chart.</div>;
  }

  const values = scaledRows.flatMap((point) => [point.plotEquity, point.plotIndex]);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue || 1;
  const paddedMin = minValue - range * 0.08;
  const paddedMax = maxValue + range * 0.08;
  const xFor = (index) => leftPad + (index / Math.max(1, scaledRows.length - 1)) * (width - leftPad - rightPad);
  const yFor = (value) => topPad + ((paddedMax - value) / (paddedMax - paddedMin || 1)) * (height - topPad - bottomPad);
  const valueForAxis = (plotValue) => (isLogScale ? 10 ** plotValue : plotValue);
  const buildPath = (key) => scaledRows.map((point, index) => `${index === 0 ? 'M' : 'L'} ${xFor(index).toFixed(2)} ${yFor(point[key]).toFixed(2)}`).join(' ');
  const ticks = 4;
  const labelEvery = Math.max(1, Math.ceil(scaledRows.length / 6));
  const last = scaledRows[scaledRows.length - 1];
  const hoverPoint = hoverIndex === null ? null : scaledRows[hoverIndex];
  const chartWidth = width - leftPad - rightPad;
  const gridStroke = COLORS.border;
  const guideStroke = COLORS.muted;
  const pointStroke = COLORS.card;
  const tooltipFill = COLORS.card;
  const onChartMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    const svgX = ratio * width;
    const relativeX = Math.min(Math.max(svgX - leftPad, 0), chartWidth);
    const nextIndex = Math.round((relativeX / Math.max(1, chartWidth)) * (scaledRows.length - 1));
    setHoverIndex(Math.min(Math.max(nextIndex, 0), scaledRows.length - 1));
  };
  const tooltipX = hoverPoint ? xFor(hoverIndex) : 0;
  const tooltipY = hoverPoint ? Math.min(yFor(Math.max(hoverPoint.plotEquity, hoverPoint.plotIndex)) + 14, height - bottomPad - 74) : 0;
  const tooltipBoxX = hoverPoint ? Math.min(Math.max(tooltipX - 92, leftPad), width - rightPad - 184) : 0;

  const watermarkLogo = themeMode === 'light' ? '/ET_App_Logo_Schild_green.png' : '/ET_App_Logo_Schild_white.png';
  const watermarkOpacity = themeMode === 'light' ? 0.07 : 0.08;

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '10px', color: COLORS.muted, fontSize: '12px' }}>
        <span><span style={{ color: COLORS.green, fontWeight: 900 }}>━</span> RSL Strategie: {formatMoney(last.equity)} {currency}</span>
        <span><span style={{ color: COLORS.cyan, fontWeight: 900 }}>━</span> {benchmark?.label || 'Index'} Buy & Hold: {formatMoney(last.index)} {currency}</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ position: 'relative', minWidth: '720px' }}>
          <img
            src={watermarkLogo}
            alt=""
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: '210px',
              maxWidth: '28%',
              height: 'auto',
              transform: 'translate(-50%, -52%)',
              opacity: watermarkOpacity,
              pointerEvents: 'none',
              userSelect: 'none',
              zIndex: 0,
            }}
          />
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ width: '100%', height: '300px', display: 'block', position: 'relative', zIndex: 1 }}
          role="img"
          aria-label="RSL Equity Chart im Vergleich zum Index"
          onMouseMove={onChartMove}
          onMouseLeave={() => setHoverIndex(null)}
        >
          {Array.from({ length: ticks + 1 }).map((_, index) => {
            const ratio = index / ticks;
            const value = paddedMin + (paddedMax - paddedMin) * (1 - ratio);
            const y = yFor(value);
            return (
              <g key={`grid-${index}`}>
                <line x1={leftPad} y1={y} x2={width - rightPad} y2={y} stroke={gridStroke} />
                <text x={leftPad - 8} y={y + 4} textAnchor="end" fontSize="11" fill={COLORS.muted}>{formatMoney(valueForAxis(value))}</text>
              </g>
            );
          })}
          <path d={buildPath('plotIndex')} fill="none" stroke={COLORS.cyan} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
          <path d={buildPath('plotEquity')} fill="none" stroke={COLORS.green} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
          {hoverPoint && (
            <g style={{ pointerEvents: 'none' }}>
              <line x1={tooltipX} y1={topPad} x2={tooltipX} y2={height - bottomPad} stroke={guideStroke} strokeDasharray="4 4" />
              <circle cx={tooltipX} cy={yFor(hoverPoint.plotIndex)} r="4.5" fill={COLORS.cyan} stroke={pointStroke} strokeWidth="2" />
              <circle cx={tooltipX} cy={yFor(hoverPoint.plotEquity)} r="5" fill={COLORS.green} stroke={pointStroke} strokeWidth="2" />
              <rect x={tooltipBoxX} y={tooltipY} width="184" height="70" rx="10" fill={tooltipFill} stroke={COLORS.border} />
              <text x={tooltipBoxX + 10} y={tooltipY + 18} fontSize="11" fontWeight="800" fill={COLORS.text}>{formatDate(hoverPoint.date)}</text>
              <text x={tooltipBoxX + 10} y={tooltipY + 38} fontSize="11" fill={COLORS.green}>RSL: {formatMoney(hoverPoint.equity)} {currency}</text>
              <text x={tooltipBoxX + 10} y={tooltipY + 56} fontSize="11" fill={COLORS.cyan}>{benchmark?.label || 'Index'}: {formatMoney(hoverPoint.index)} {currency}</text>
            </g>
          )}
          {scaledRows.map((point, index) => (
            index % labelEvery === 0 || index === scaledRows.length - 1 ? (
              <text key={`x-${point.date}`} x={xFor(index)} y={height - 14} textAnchor="middle" fontSize="11" fill={COLORS.muted}>
                {formatDate(point.date)}
              </text>
            ) : null
          ))}
        </svg>
        </div>
      </div>
    </div>
  );
}

function StrategyComparisonChart({ variants, indexCurve, benchmark, COLORS, currency, themeMode = 'dark', scaleMode = 'linear', showLegend = true, maxTooltipItems = null, formatValue = formatMoney }) {
  const svgRef = useRef(null);
  const [hoverPoint, setHoverPoint] = useState(null);
  const isLogScale = scaleMode === 'log';
  const width = 920;
  const height = 320;
  const leftPad = 62;
  const rightPad = 24;
  const topPad = 22;
  const bottomPad = 44;
  const paletteByCode = {
    S1: '#1fd1a5',
    K1: '#4f8cff',
    K2: '#61d66f',
    K3: '#ffb02e',
    K4: '#ff5a5f',
    D1: '#b38cff',
    D2: '#35d6c6',
    D3: '#ff7a1a',
    D4: '#ff66d8',
    ES1: '#7dd3fc',
    EK1: '#a7f3d0',
    EK2: '#bef264',
    EK3: '#fcd34d',
    EK4: '#fb7185',
    ED1: '#c4b5fd',
    ED2: '#67e8f9',
    ED3: '#fdba74',
    ED4: '#f0abfc',
  };
  const fallbackPalette = ['#1fd1a5', '#4f8cff', '#61d66f', '#ffb02e', '#ff5a5f', '#b38cff', '#35d6c6', '#ff7a1a', '#ff66d8'];
  const rows = (variants || [])
    .map((variant, index) => ({
      ...variant,
      color: paletteByCode[variant.code] || fallbackPalette[index % fallbackPalette.length],
      points: (variant.equity_curve || [])
        .map((point) => ({ date: point.date, equity: Number(point.equity) }))
        .filter((point) => Number.isFinite(point.equity)),
    }))
    .filter((variant) => variant.points.length > 1);
  const referencePoints = (indexCurve || [])
    .map((point) => ({ date: point.date, equity: Number(point.value) }))
    .filter((point) => Number.isFinite(point.equity));
  const referenceRow = referencePoints.length > 1
    ? {
        code: 'INDEX',
        label: `${benchmark?.label || 'Index'} Buy & Hold`,
        color: '#22d3ee',
        points: referencePoints,
        isReference: true,
      }
    : null;
  const chartRows = referenceRow ? [...rows, referenceRow] : rows;
  const scaledChartRows = chartRows
    .map((variant) => ({
      ...variant,
      points: variant.points
        .filter((point) => !isLogScale || point.equity > 0)
        .map((point) => ({
          ...point,
          plotValue: isLogScale ? Math.log10(point.equity) : point.equity,
        })),
    }))
    .filter((variant) => variant.points.length > 1);
  const baseRows = rows
    .map((variant) => ({ ...variant, points: variant.points.filter((point) => !isLogScale || point.equity > 0) }))
    .filter((variant) => variant.points.length > 1);
  const scaledReferencePoints = referencePoints
    .filter((point) => !isLogScale || point.equity > 0)
    .map((point) => ({ ...point, plotValue: isLogScale ? Math.log10(point.equity) : point.equity }));
  const basePoints = baseRows.reduce((longest, variant) => (
    variant.points.length > longest.length ? variant.points : longest
  ), scaledReferencePoints.length > 1 ? scaledReferencePoints : []);

  if (scaledChartRows.length === 0 || basePoints.length < 2) {
    return <div style={{ color: COLORS.muted, fontSize: '13px' }}>Noch zu wenige Datenpunkte für den Vergleichschart.</div>;
  }

  const values = scaledChartRows.flatMap((variant) => variant.points.map((point) => point.plotValue));
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue || 1;
  const paddedMin = minValue - range * 0.08;
  const paddedMax = maxValue + range * 0.08;
  const xFor = (index, length) => leftPad + (index / Math.max(1, length - 1)) * (width - leftPad - rightPad);
  const yFor = (value) => topPad + ((paddedMax - value) / (paddedMax - paddedMin || 1)) * (height - topPad - bottomPad);
  const valueForAxis = (plotValue) => (isLogScale ? 10 ** plotValue : plotValue);
  const buildPath = (points) => points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${xFor(index, points.length).toFixed(2)} ${yFor(point.plotValue).toFixed(2)}`).join(' ');
  const labelEvery = Math.max(1, Math.ceil(basePoints.length / 7));
  const watermarkLogo = themeMode === 'light' ? '/ET_App_Logo_Schild_green.png' : '/ET_App_Logo_Schild_white.png';
  const watermarkOpacity = themeMode === 'light' ? 0.07 : 0.08;
  const handlePointerMove = (event) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const svgX = ((event.clientX - rect.left) / rect.width) * width;
    const nearestIndex = Math.round(
      ((svgX - leftPad) / Math.max(1, width - leftPad - rightPad)) * (basePoints.length - 1),
    );
    if (nearestIndex < 0 || nearestIndex >= basePoints.length) {
      setHoverPoint(null);
      return;
    }
    const date = basePoints[nearestIndex]?.date;
    const valuesAtDate = scaledChartRows
      .map((variant) => {
        const exactIndex = variant.points.findIndex((point) => point.date === date);
        const pointIndex = exactIndex >= 0
          ? exactIndex
          : Math.min(
              variant.points.length - 1,
              Math.round((nearestIndex / Math.max(1, basePoints.length - 1)) * (variant.points.length - 1)),
            );
        const point = variant.points[pointIndex];
        return { variant, point, x: xFor(pointIndex, variant.points.length), y: yFor(point.plotValue) };
      })
      .filter((item) => item.point)
      .sort((a, b) => b.point.equity - a.point.equity);
    const limitedValues = Number.isFinite(Number(maxTooltipItems)) && Number(maxTooltipItems) > 0
      ? valuesAtDate.slice(0, Number(maxTooltipItems))
      : valuesAtDate;
    setHoverPoint({
      date,
      x: xFor(nearestIndex, basePoints.length),
      values: limitedValues,
      totalValues: valuesAtDate.length,
    });
  };

  return (
    <div>
      {showLegend && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 14px', marginBottom: '12px', color: COLORS.muted, fontSize: '12px' }}>
          {scaledChartRows.map((variant) => {
            const last = variant.points[variant.points.length - 1];
            return (
              <span key={variant.code}>
                <span style={{ color: variant.color, fontWeight: 900 }}>━</span> {variant.displayCode || variant.code} {variant.label}: {formatValue(last.equity)} {currency}
              </span>
            );
          })}
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ position: 'relative', minWidth: '760px' }}>
          <img
            src={watermarkLogo}
            alt=""
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: '230px',
              maxWidth: '28%',
              height: 'auto',
              transform: 'translate(-50%, -52%)',
              opacity: watermarkOpacity,
              pointerEvents: 'none',
              userSelect: 'none',
              zIndex: 0,
            }}
          />
          <svg
            ref={svgRef}
            viewBox={`0 0 ${width} ${height}`}
            style={{ width: '100%', height: '340px', display: 'block', position: 'relative', zIndex: 1 }}
            role="img"
            aria-label="Strategie-Vergleich mit ausgewählten Backtest-Kurven und Index-Referenz"
            onMouseMove={handlePointerMove}
            onMouseLeave={() => setHoverPoint(null)}
          >
            {Array.from({ length: 5 }).map((_, index) => {
              const ratio = index / 4;
              const value = paddedMin + (paddedMax - paddedMin) * (1 - ratio);
              const y = yFor(value);
              return (
                <g key={`grid-${index}`}>
                  <line x1={leftPad} y1={y} x2={width - rightPad} y2={y} stroke={COLORS.border} />
                  <text x={leftPad - 8} y={y + 4} textAnchor="end" fontSize="11" fill={COLORS.muted}>{formatValue(valueForAxis(value))}</text>
                </g>
              );
            })}
            {scaledChartRows.map((variant) => (
              <path
                key={variant.code}
                d={buildPath(variant.points)}
                fill="none"
                stroke={variant.color}
                strokeWidth={variant.isReference ? '1.45' : '1.75'}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={variant.isReference ? '0.78' : '0.95'}
                strokeDasharray={variant.isReference ? '7 5' : undefined}
              />
            ))}
            {hoverPoint && (
              <g pointerEvents="none">
                <line x1={hoverPoint.x} y1={topPad} x2={hoverPoint.x} y2={height - bottomPad} stroke={COLORS.muted} strokeDasharray="4 5" opacity="0.45" />
                {hoverPoint.values.map((item) => (
                  <circle
                    key={`${item.variant.code}-${item.point.date}`}
                    cx={item.x}
                    cy={item.y}
                    r="3.4"
                    fill={item.variant.color}
                    stroke={COLORS.bg || '#06131a'}
                    strokeWidth="1.6"
                  />
                ))}
              </g>
            )}
            {basePoints.map((point, index) => (
              index % labelEvery === 0 || index === basePoints.length - 1 ? (
                <text key={`x-${point.date}`} x={xFor(index, basePoints.length)} y={height - 14} textAnchor="middle" fontSize="11" fill={COLORS.muted}>
                  {formatDate(point.date)}
                </text>
              ) : null
            ))}
          </svg>
          {hoverPoint && (
            <div
              style={{
                position: 'absolute',
                left: `${Math.min(82, Math.max(12, (hoverPoint.x / width) * 100))}%`,
                top: '45%',
                transform: hoverPoint.x > width * 0.72 ? 'translate(-100%, -50%)' : 'translate(10px, -50%)',
                background: '#0b1d26f2',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                padding: '9px 10px',
                color: COLORS.text,
                fontSize: '12px',
                lineHeight: 1.45,
                pointerEvents: 'none',
                zIndex: 3,
                boxShadow: '0 10px 28px rgba(0,0,0,0.32)',
                minWidth: '230px',
                maxWidth: '320px',
              }}
            >
              <div style={{ fontWeight: 900, marginBottom: '7px' }}>{formatDate(hoverPoint.date)}</div>
              <div style={{ display: 'grid', gap: '4px' }}>
                {hoverPoint.values.map((item) => (
                  <div key={`${item.variant.code}-${item.point.date}`} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px', alignItems: 'baseline' }}>
                    <span style={{ color: item.variant.color, fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.variant.displayCode || item.variant.code} {item.variant.label}
                    </span>
                    <span style={{ color: COLORS.text, fontWeight: 800, whiteSpace: 'nowrap' }}>
                      {formatValue(item.point.equity)}
                    </span>
                  </div>
                ))}
              </div>
              {hoverPoint.totalValues > hoverPoint.values.length && (
                <div style={{ color: COLORS.muted, marginTop: '6px', fontSize: '11px' }}>
                  Top {hoverPoint.values.length} von {hoverPoint.totalValues} Kurven
                </div>
              )}
              <div style={{ color: COLORS.muted, marginTop: '5px', fontSize: '11px' }}>{currency}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RslBacktestSection({
  COLORS,
  th,
  td,
  inputStyle: sharedInputStyle,
  onSessionExpired,
  canExportCsv = true,
  canAccessExtendedMarkets = true,
  onBlockedFeature,
  themeMode = 'dark',
  isAdmin = false,
}) {
  const { Card, Btn, Badge } = useMemo(() => buildUiHelpers({ COLORS, th: {} }), [COLORS]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tradeSort, setTradeSort] = useState({ key: null, direction: 'asc' });
  const [parameterSort, setParameterSort] = useState({ key: null, direction: 'asc' });
  const [executionMode, setExecutionMode] = useState(DEFAULT_SELECTABLE_BACKTEST_MODE);
  const [selectedStrategyCodes, setSelectedStrategyCodes] = useState(DEFAULT_STRATEGY_COMPARISON_CODES);
  const [parameterPositions, setParameterPositions] = useState([]);
  const [parameterExitCriteria, setParameterExitCriteria] = useState([]);
  const [parameterExitPercents, setParameterExitPercents] = useState([]);
  const [dynamicSizing, setDynamicSizing] = useState(true);
  const [comparisonChartScale, setComparisonChartScale] = useState('linear');
  const normalText = { letterSpacing: 'normal', wordSpacing: 'normal', textTransform: 'none' };
  const isHistoricalStrategyMode = executionMode === 'historical_strategy_comparison_v09' || executionMode === 'historical_strategy_comparison_v10';
  const isAdminSignalMode = executionMode === 'historical_admin_signal_v11';
  const isParameterComparisonMode = executionMode === 'parameter_comparison';
  const isStrategySelectionMode = isHistoricalStrategyMode;
  const visibleStrategyVariants = isHistoricalStrategyMode
    ? HISTORICAL_STRATEGY_COMPARISON_VARIANTS
    : STRATEGY_COMPARISON_VARIANTS;
  const defaultVisibleStrategyCodes = isHistoricalStrategyMode
    ? DEFAULT_HISTORICAL_STRATEGY_COMPARISON_CODES
    : DEFAULT_STRATEGY_COMPARISON_CODES;
  const marketOptions = useMemo(() => (
    isParameterComparisonMode
      ? [
        ['SP500', 'S&P 500'],
        ['NASDAQ100', 'Nasdaq 100'],
        ['HDAX', 'HDAX'],
        ['STOXX600', 'STOXX Europe 600'],
      ]
      : isAdminSignalMode
      ? [
        ['SP500', 'S&P 500'],
        ['HDAX', 'HDAX'],
        ['NASDAQ100', 'Nasdaq 100'],
        ['STOXX600', 'STOXX Europe 600'],
      ]
      : isHistoricalStrategyMode
      ? [
        ['SP500', 'S&P 500'],
        ['HDAX', 'HDAX'],
        ['NASDAQ100', 'Nasdaq 100'],
        ['RUSSELL2000', 'Russell 2000'],
        ['DJI', 'Dow Jones'],
      ]
      : [
        ['SP500', 'S&P 500'],
        ['HDAX', 'HDAX'],
        ['NASDAQ100', 'Nasdaq 100'],
        ['STOXX600', 'STOXX Europe 600'],
      ]
  ), [isAdminSignalMode, isHistoricalStrategyMode, isParameterComparisonMode]);

  const inputStyle = {
    ...(sharedInputStyle || {}),
    width: '100%',
    height: '42px',
    boxSizing: 'border-box',
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    padding: '9px 11px',
    marginBottom: 0,
    letterSpacing: 'normal',
    wordSpacing: 'normal',
    textTransform: 'none',
  };
  const fieldLabelStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    gap: '6px',
    minHeight: '66px',
    color: COLORS.muted,
    fontSize: '12px',
    ...normalText,
  };
  const toggleStrategyCode = (code) => {
    setSelectedStrategyCodes((current) => (
      current.includes(code)
        ? current.filter((item) => item !== code)
        : [...current, code]
    ));
  };
  const toggleNumberSelection = (value, setter) => {
    setter((current) => (
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value].sort((a, b) => a - b)
    ));
  };
  const toggleStringSelection = (value, setter) => {
    setter((current) => (
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    ));
  };
  const resetParameterComparisonSelection = useCallback(() => {
    setParameterPositions([]);
    setParameterExitCriteria([]);
    setParameterExitPercents([]);
    setParameterSort({ key: null, direction: 'asc' });
  }, []);

  const canUseSp5001996History = false;
  const backtestMinStartDate = isParameterComparisonMode
    ? '1999-04-01'
    : isHistoricalStrategyMode || isAdminSignalMode
    ? form.market === 'HDAX'
      ? HDAX_MIN_HISTORY_DATE
      : BACKTEST_NORGATE_MIN_START_DATE
    : canUseSp5001996History
    ? BACKTEST_SP500_1996_MIN_START_DATE
    : (BACKTEST_MARKET_MIN_START_DATES[form.market] || BACKTEST_DEFAULT_MIN_START_DATE);
  const formattedBacktestMinStartDate = formatDate(backtestMinStartDate);
  const periodOptions = useMemo(() => (
    isHistoricalStrategyMode || isAdminSignalMode || isParameterComparisonMode
      ? HISTORICAL_PERIOD_OPTIONS
      : [
        ...DEFAULT_PERIOD_OPTIONS,
        ...(canUseSp5001996History
          ? [['MAX', 'Seit 1996']]
          : []),
        ['CUSTOM', 'Benutzerdefiniert'],
      ]
  ), [canUseSp5001996History, isAdminSignalMode, isHistoricalStrategyMode, isParameterComparisonMode]);

  useEffect(() => {
    let isMounted = true;
    apiRequest('/app/rsl-backtest-mode')
      .then((data) => {
        if (!isMounted) return;
        const mode = data.execution_mode || DEFAULT_SELECTABLE_BACKTEST_MODE;
        setExecutionMode(normalizeSelectableBacktestMode(mode));
      })
      .catch(() => {
        if (isMounted) setExecutionMode(DEFAULT_SELECTABLE_BACKTEST_MODE);
      });
    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  useEffect(() => {
    setForm((current) => {
      if (current.period !== 'CUSTOM' || !current.start_date || current.start_date >= backtestMinStartDate) {
        return current;
      }
      return { ...current, start_date: backtestMinStartDate };
    });
  }, [backtestMinStartDate]);

  useEffect(() => {
    setForm((current) => {
      if (current.period !== 'MAX' || canUseSp5001996History || isHistoricalStrategyMode || isAdminSignalMode) {
        return current;
      }
      return { ...current, period: '5Y' };
    });
  }, [canUseSp5001996History, isAdminSignalMode, isHistoricalStrategyMode]);

  useEffect(() => {
    if (isAdminSignalMode || isParameterComparisonMode) {
      setForm((current) => (current.period === 'MAX' ? current : { ...current, period: 'MAX' }));
      return;
    }
    setForm((current) => (
      periodOptions.some(([value]) => value === current.period)
        ? current
        : { ...current, period: '5Y' }
    ));
  }, [isAdminSignalMode, isParameterComparisonMode, periodOptions]);

  useEffect(() => {
    if (isHistoricalStrategyMode) {
      setSelectedStrategyCodes(DEFAULT_HISTORICAL_STRATEGY_COMPARISON_CODES);
    }
  }, [executionMode, isHistoricalStrategyMode]);

  useEffect(() => {
    if (isParameterComparisonMode) {
      resetParameterComparisonSelection();
      setResult(null);
      setError('');
    }
  }, [isParameterComparisonMode, resetParameterComparisonSelection]);

  useEffect(() => {
    setForm((current) => {
      const allowedMarkets = new Set(marketOptions.map(([value]) => value));
      const fallbackMarket = marketOptions[0]?.[0] || 'SP500';
      return allowedMarkets.has(current.market) ? current : { ...current, market: fallbackMarket };
    });
  }, [marketOptions]);

  const runBacktest = async () => {
    if (loading) return;
    if (isParameterComparisonMode) {
      if (parameterPositions.length === 0) {
        setError('Bitte mindestens eine Positionsanzahl auswählen.');
        return;
      }
      if (parameterExitCriteria.length === 0) {
        setError('Bitte mindestens ein Ausstiegskriterium auswählen.');
        return;
      }
      if (parameterExitCriteria.includes('k_exit') && parameterExitPercents.length === 0) {
        setError('Bitte mindestens einen Top-%-Wert auswählen.');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const data = await apiRequest('/admin/rsl-parameter-comparison', {
          method: 'POST',
          timeoutMs: BACKTEST_REQUEST_TIMEOUT_MS,
          body: {
            market: form.market,
            positions_counts: parameterPositions,
            exit_criteria: parameterExitCriteria,
            exit_percent_filters: parameterExitPercents,
          },
        });
        setResult(data);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          onSessionExpired?.();
          return;
        }
        setError(err?.message || 'Parameter-Vergleich konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (form.period === 'CUSTOM' && form.start_date && form.start_date < backtestMinStartDate) {
      setError(`Der Backtest kann frühestens am ${formattedBacktestMinStartDate} starten.`);
      return;
    }
    if (isStrategySelectionMode && selectedStrategyCodes.length === 0) {
      setError('Bitte mindestens eine Strategie-Variante auswählen.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const body = {
        market: form.market,
        period: isAdminSignalMode ? 'MAX' : form.period,
        positions_count: Number(form.positions_count),
        sector_positions_count: Number(form.sector_positions_count),
        amount_per_position: Number(form.amount_per_position),
        fee_per_trade: Number(form.fee_per_trade),
        max_per_industry: isAdminSignalMode ? 0 : Number(form.max_per_industry),
      };
      if (isAdmin) {
        body.execution_mode = executionMode;
      }
      if (isStrategySelectionMode) {
        body.strategy_codes = selectedStrategyCodes;
      }
      if (!isAdminSignalMode && form.period === 'CUSTOM') {
        body.start_date = form.start_date || null;
        body.end_date = form.end_date || null;
      }
      if (isHistoricalStrategyMode) {
        body.dynamic_sizing = dynamicSizing;
        body.classic_exit_rank_pct = Number(form.classic_exit_rank_pct || 25);
      }
      const data = await apiRequest('/admin/rsl-backtest', {
        method: 'POST',
        timeoutMs: isStrategySelectionMode || isAdminSignalMode || canUseSp5001996History
          ? STRATEGY_COMPARISON_REQUEST_TIMEOUT_MS
          : form.market === 'NASDAQ100' || form.market === 'STOXX600'
          ? EXTENDED_BACKTEST_REQUEST_TIMEOUT_MS
          : BACKTEST_REQUEST_TIMEOUT_MS,
        body,
      });
      setResult(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      setError(err?.message || 'Backtest konnte nicht berechnet werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleMarketChange = event => {
    const nextMarket = event.target.value;
    const isExtendedMarket = nextMarket === 'NASDAQ100' || nextMarket === 'STOXX600';
    if (isExtendedMarket && !canAccessExtendedMarkets) {
      onBlockedFeature?.('Nasdaq 100 und STOXX 600');
      return;
    }
    setForm((current) => ({ ...current, market: nextMarket }));
    if (isParameterComparisonMode) {
      resetParameterComparisonSelection();
      setResult(null);
      setError('');
    }
  };

  const summary = result?.summary || {};
  const currency = result?.currency || (form.market === 'HDAX' ? 'EUR' : 'USD');
  const trades = Array.isArray(result?.trades) ? result.trades : EMPTY_SERIES;
  const equityCurve = Array.isArray(result?.equity_curve) ? result.equity_curve : EMPTY_SERIES;
  const indexCurve = Array.isArray(result?.index_curve) ? result.index_curve : EMPTY_SERIES;
  const comparison = result?.comparison || {};
  const comparisonVariants = Array.isArray(comparison?.variants) ? comparison.variants : EMPTY_SERIES;
  const isComparisonResult = comparison?.type === 'strategy_comparison';
  const isParameterComparisonResult = comparison?.type === 'parameter_comparison';
  const hasSectorComparisonVariant = comparisonVariants.some((variant) => variant.universe === 'sector_etfs');
  const benchmark = result?.benchmark || {};
  const benchmarkStartValue = Number(indexCurve[0]?.value);
  const benchmarkEndValue = Number(indexCurve[indexCurve.length - 1]?.value);
  const benchmarkTotalPercent = Number.isFinite(benchmarkStartValue) && benchmarkStartValue !== 0 && Number.isFinite(benchmarkEndValue)
    ? ((benchmarkEndValue / benchmarkStartValue) - 1) * 100
    : null;
  const parameterBenchmarkSummary = useMemo(() => calculateCurveStats(indexCurve), [indexCurve]);
  const classicExitRankPct = Number(result?.parameters?.classic_exit_rank_pct || form.classic_exit_rank_pct || 25);
  const resultMarketLabel = MARKET_LABELS[result?.market] || result?.market || 'S&P 500';
  const v11DataStart = BACKTEST_V11_DATA_START[form.market];
  const v11HistoryNote = isAdminSignalMode && v11DataStart
    ? `Hinweis: Index ${v11DataStart.label} nutzt Datenbasis ab ${v11DataStart.date}. Frühere Zeiträume starten automatisch ab diesem Datenstand.`
    : '';
  const hdaxHistoryNote = form.market === 'HDAX' && !isAdminSignalMode && !isParameterComparisonMode
    ? isHistoricalStrategyMode
      ? 'Hinweis: HDAX nutzt in v0.9/v1.0 die v0.2-Datenbasis ab 2024. Frühere Zeiträume starten automatisch ab diesem Datenstand.'
      : 'Hinweis: Fuer HDAX liegt die historische Zusammensetzung aktuell erst ab 2024 vor. Fruehere Zeitraeume starten automatisch ab diesem Datenstand.'
    : '';

  const tradeColumns = useMemo(() => ([
    { label: 'Ticker', key: 'symbol', type: 'string' },
    { label: 'Branche', key: 'industry', type: 'string' },
    { label: 'Kauf', key: 'entry_date', type: 'date' },
    { label: 'Kaufkurs', key: 'entry_price', type: 'number' },
    { label: 'Rang', key: 'entry_rank', type: 'number' },
    { label: 'RSL Kauf', key: 'entry_rsl', type: 'number' },
    { label: 'Verkauf', key: 'exit_date', type: 'date' },
    { label: 'Verkaufskurs', key: 'exit_price', type: 'number' },
    { label: 'Tage', key: 'days', type: 'number' },
    { label: 'P/L', key: 'realized_pl', type: 'number' },
    { label: 'P/L %', key: 'realized_pl_percent', type: 'number' },
    { label: 'Grund', key: 'exit_reason', type: 'string' },
  ]), []);
  const parameterColumns = useMemo(() => ([
    { label: 'Variante', key: 'code', type: 'string' },
    { label: 'Positionen', key: 'positions_count', type: 'number' },
    { label: 'Ausstieg', key: 'exit_label', type: 'string' },
    { label: 'Endwert', key: 'summary.end_value', type: 'number' },
    { label: 'Gesamt %', key: 'summary.total_pl_percent', type: 'number' },
    { label: 'ROR', key: 'summary.ror', type: 'number' },
    { label: 'Max DD', key: 'summary.max_drawdown', type: 'number' },
    { label: 'MAR', key: 'summary.mar', type: 'number' },
    { label: 'Sharpe', key: 'summary.sharpe', type: 'number' },
    { label: 'Profit-Faktor', key: 'summary.profit_factor', type: 'number' },
    { label: 'Trades', key: 'summary.closed_trades', type: 'number' },
  ]), []);

  const sortedTrades = useMemo(() => {
    if (!tradeSort.key) return trades;
    const column = tradeColumns.find(col => col.key === tradeSort.key);
    if (!column) return trades;
    const directionFactor = tradeSort.direction === 'desc' ? -1 : 1;
    const toComparable = (trade) => {
      const value = trade?.[column.key];
      if (value === null || value === undefined || value === '') return null;
      if (column.type === 'number') {
        const number = Number(value);
        return Number.isFinite(number) ? number : null;
      }
      if (column.type === 'date') {
        const timestamp = Date.parse(value);
        return Number.isFinite(timestamp) ? timestamp : null;
      }
      return String(value).toLowerCase();
    };

    return [...trades].sort((a, b) => {
      const aVal = toComparable(a);
      const bVal = toComparable(b);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (aVal < bVal) return -1 * directionFactor;
      if (aVal > bVal) return 1 * directionFactor;
      return 0;
    });
  }, [tradeColumns, tradeSort.direction, tradeSort.key, trades]);

  const toggleTradeSort = (key) => {
    setTradeSort((current) => {
      if (current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'asc' };
    });
  };
  const sortedParameterComparisonVariants = useMemo(() => {
    if (!parameterSort.key) return comparisonVariants;
    const column = parameterColumns.find(col => col.key === parameterSort.key);
    if (!column) return comparisonVariants;
    const directionFactor = parameterSort.direction === 'desc' ? -1 : 1;
    const valueFor = (variant) => {
      if (column.key === 'exit_label') {
        return variant.exit_criterion === 'index_rsl'
          ? 'Index-RSL'
          : `Top ${variant.exit_percent_filter}% im Index`;
      }
      const value = column.key.split('.').reduce((current, key) => current?.[key], variant);
      if (value === null || value === undefined || value === '') return null;
      if (column.type === 'number') {
        const number = Number(value);
        return Number.isFinite(number) ? number : null;
      }
      return String(value).toLowerCase();
    };
    return [...comparisonVariants].sort((a, b) => {
      const aVal = valueFor(a);
      const bVal = valueFor(b);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (aVal < bVal) return -1 * directionFactor;
      if (aVal > bVal) return 1 * directionFactor;
      return 0;
    });
  }, [comparisonVariants, parameterColumns, parameterSort.direction, parameterSort.key]);
  const parameterComparisonChartVariants = useMemo(() => (
    comparisonVariants.map((variant) => ({
      ...variant,
      displayCode: parameterComparisonDisplayCode(variant.code),
      label: parameterComparisonVariantLabel(variant),
    }))
  ), [comparisonVariants]);

  const applyModelPortfolioParameterPreset = () => {
    const preset = PARAMETER_COMPARISON_MODEL_PRESETS[form.market] || PARAMETER_COMPARISON_MODEL_PRESETS.SP500;
    setParameterPositions(preset.positions);
    setParameterExitCriteria(preset.exitCriteria);
    setParameterExitPercents(preset.exitPercents);
  };

  const toggleParameterSort = (key) => {
    setParameterSort((current) => {
      if (current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'asc' };
    });
  };

  const toCsvCell = (value) => {
    const text = String(value ?? '');
    if (!/[;"\n\r]/.test(text)) return text;
    return `"${text.replace(/"/g, '""')}"`;
  };

  const toCsvRow = (fields) => fields.map(toCsvCell).join(';');
  const csvFilenamePart = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'export';
  const formatCsvMoney = (value) => (Number.isFinite(Number(value)) ? formatMoney(value) : '');
  const formatCsvPercent = (value) => (Number.isFinite(Number(value)) ? formatPercent(value) : '');
  const formatCsvDate = (value) => (value ? formatDate(value) : '');
  const formatCsvRsl = (value) => (
    Number.isFinite(Number(value))
      ? Number(value).toLocaleString('de-DE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
      : ''
  );

  const buildTradeCsv = ({ summaryRows, rows }) => {
    const tradeRows = [
      '',
      toCsvRow(['Ticker', 'Status', 'Branche', 'Kaufdatum', 'Kaufkurs', 'Rang', 'RSL Kauf', 'Verkaufsdatum', 'Verkaufskurs', 'Tage', 'P/L', 'P/L %', 'Grund']),
      ...(Array.isArray(rows) ? rows : []).map((trade) => {
        const isOpen = trade.status === 'open' || !trade.exit_date;
        return toCsvRow([
          trade.symbol || '',
          isOpen ? 'Offen' : 'Geschlossen',
          trade.industry || '',
          formatCsvDate(trade.entry_date),
          formatCsvMoney(trade.entry_price),
          trade.entry_rank ?? '',
          formatCsvRsl(trade.entry_rsl),
          isOpen ? '' : formatCsvDate(trade.exit_date),
          isOpen ? '' : formatCsvMoney(trade.exit_price),
          trade.days ?? '',
          formatCsvMoney(trade.realized_pl),
          formatCsvPercent(trade.realized_pl_percent),
          trade.exit_reason || (isOpen ? 'Offen' : ''),
        ]);
      }),
    ];
    return ['\uFEFF', [...summaryRows, ...tradeRows].join('\n')].join('');
  };

  const exportBacktestCsv = () => {
    if (!canExportCsv) {
      onBlockedFeature?.('CSV-Export im Backtest');
      return;
    }
    if (!result) return;
    const summaryRows = [
      toCsvRow(['Kennzahl', 'Wert']),
      toCsvRow(['Markt', resultMarketLabel]),
      toCsvRow(['Zeitraum', result.period || '']),
      toCsvRow(['Startdatum', result.start_date || '']),
      toCsvRow(['Enddatum', result.end_date || '']),
      toCsvRow(['Währung', currency]),
      toCsvRow(['Startkapital', formatMoney(summary.start_capital)]),
      toCsvRow(['Endwert', formatMoney(summary.end_value)]),
      toCsvRow(['Gesamt P/L', formatMoney(summary.total_pl)]),
      toCsvRow(['Gesamt P/L %', formatPercent(summary.total_pl_percent)]),
      toCsvRow(['Realisiert', formatMoney(summary.realized_pl)]),
      toCsvRow(['Buch P/L', formatMoney(summary.book_pl)]),
      toCsvRow(['Trades geschlossen', summary.closed_trades || 0]),
      toCsvRow(['Trades offen', summary.open_trades || 0]),
      toCsvRow(['Win-Rate', formatPercent(summary.win_rate)]),
      toCsvRow(['Max Drawdown', formatPercent(summary.max_drawdown)]),
      toCsvRow([`${benchmark.label || 'Index'} Endwert`, formatMoney(summary.index_end_value)]),
      toCsvRow([`${benchmark.label || 'Index'} P/L %`, formatPercent(summary.index_pl_percent)]),
      toCsvRow(['Alpha vs Index', formatMoney(summary.alpha_vs_index)]),
      toCsvRow(['Alpha vs Index %', formatPercent(summary.alpha_vs_index_percent)]),
    ];

    const blob = new Blob([buildTradeCsv({ summaryRows, rows: trades })], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rsl-backtest-${(result?.market || form.market || 'market').toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const exportVariantCsv = (variant) => {
    if (!canExportCsv) {
      onBlockedFeature?.('CSV-Export im Backtest');
      return;
    }
    if (!result || !variant) return;
    const variantSummary = variant.summary || {};
    const summaryRows = [
      toCsvRow(['Kennzahl', 'Wert']),
      toCsvRow(['Markt', resultMarketLabel]),
      toCsvRow(['Variante', variant.code || '']),
      toCsvRow(['Name', variant.label || '']),
      toCsvRow(['Universum', variant.universe === 'sector_etfs' ? 'Sektor ETFs' : 'Einzeltitel']),
      toCsvRow(['Zeitraum', result.period || '']),
      toCsvRow(['Startdatum', result.start_date || '']),
      toCsvRow(['Enddatum', result.end_date || '']),
      toCsvRow(['Währung', currency]),
      toCsvRow(['Startkapital', formatMoney(variantSummary.start_capital)]),
      toCsvRow(['Endwert', formatMoney(variantSummary.end_value)]),
      toCsvRow(['Gesamt P/L', formatMoney(variantSummary.total_pl)]),
      toCsvRow(['Gesamt P/L %', formatPercent(variantSummary.total_pl_percent)]),
      toCsvRow(['Trades geschlossen', variantSummary.closed_trades || 0]),
      toCsvRow(['Trades offen', variantSummary.open_trades || 0]),
      toCsvRow(['Win-Rate', formatPercent(variantSummary.win_rate)]),
      toCsvRow(['Max Drawdown', formatPercent(variantSummary.max_drawdown)]),
      toCsvRow(['CAGR', formatPercent(variantSummary.cagr)]),
      toCsvRow(['Zeit im Markt', formatPercent(variantSummary.time_in_market_percent)]),
      toCsvRow(['Beste Woche', formatPercent(variantSummary.best_week_percent)]),
      toCsvRow(['Schlechteste Woche', formatPercent(variantSummary.worst_week_percent)]),
    ];
    const blob = new Blob([buildTradeCsv({ summaryRows, rows: variant.trades || [] })], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rsl-backtest-${csvFilenamePart(result?.market || form.market)}-${csvFilenamePart(variant.code)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div style={normalText}>
      <style>{`
        @keyframes rsl-backtest-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <Card style={{ marginBottom: '18px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', alignItems: 'end' }}>
          {isAdmin && (
            <label style={fieldLabelStyle}>
              Backtest-Modus
              <select value={executionMode} onChange={(event) => setExecutionMode(event.target.value)} style={inputStyle}>
                {BACKTEST_MODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          )}
          <label style={fieldLabelStyle}>
            Markt
            <select value={form.market} onChange={handleMarketChange} style={inputStyle}>
              {marketOptions.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          {!isAdminSignalMode && !isParameterComparisonMode && (
            <label style={fieldLabelStyle}>
              Zeitraum
              <select value={form.period} onChange={(event) => setForm((current) => ({ ...current, period: event.target.value }))} style={inputStyle}>
                {periodOptions.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
          )}
          {!isAdminSignalMode && !isParameterComparisonMode && form.period === 'CUSTOM' && (
            <>
              <label style={fieldLabelStyle}>
                Startdatum
                <input type="date" min={backtestMinStartDate} value={form.start_date} onChange={(event) => setForm((current) => ({ ...current, start_date: event.target.value }))} style={inputStyle} />
              </label>
              <label style={fieldLabelStyle}>
                Enddatum
                <input type="date" value={form.end_date} onChange={(event) => setForm((current) => ({ ...current, end_date: event.target.value }))} style={inputStyle} />
              </label>
            </>
          )}
          {!isParameterComparisonMode && (
          <label style={fieldLabelStyle}>
            Anzahl gleichzeitiger Titel
            <input type="number" min="1" max="50" value={form.positions_count} onChange={(event) => setForm((current) => ({ ...current, positions_count: event.target.value }))} style={inputStyle} />
          </label>
          )}
          {isHistoricalStrategyMode && (
            <label style={fieldLabelStyle}>
              Anzahl Sektor-ETFs
              <input type="number" min="1" max="11" value={form.sector_positions_count} onChange={(event) => setForm((current) => ({ ...current, sector_positions_count: event.target.value }))} style={inputStyle} />
            </label>
          )}
          {!isParameterComparisonMode && (
          <label style={fieldLabelStyle}>
            Invest je Titel EUR/USD
            <input type="number" min="1" step="100" value={form.amount_per_position} onChange={(event) => setForm((current) => ({ ...current, amount_per_position: event.target.value }))} style={inputStyle} />
          </label>
          )}
          {!isParameterComparisonMode && (
          <label style={fieldLabelStyle}>
            Gebühr je Trade EUR/USD
            <input type="number" min="0" step="1" value={form.fee_per_trade} onChange={(event) => setForm((current) => ({ ...current, fee_per_trade: event.target.value }))} style={inputStyle} />
          </label>
          )}
          {!isAdminSignalMode && !isParameterComparisonMode && (
            <label style={fieldLabelStyle}>
              Branchenfilter
              <input type="number" min="0" max="50" step="1" value={form.max_per_industry} onChange={(event) => setForm((current) => ({ ...current, max_per_industry: event.target.value }))} style={inputStyle} title="Maximale Anzahl offener Titel aus derselben Branche. 0 bedeutet keine Begrenzung." />
            </label>
          )}
          {isHistoricalStrategyMode && (
            <label style={fieldLabelStyle}>
              Top % im Index
              <select value={form.classic_exit_rank_pct} onChange={(event) => setForm((current) => ({ ...current, classic_exit_rank_pct: event.target.value }))} style={inputStyle}>
                {CLASSIC_EXIT_RANK_PERCENT_OPTIONS.map((percent) => (
                  <option key={percent} value={percent}>Top {percent}%</option>
                ))}
              </select>
            </label>
          )}
          <Btn color="green" onClick={runBacktest} disabled={loading} style={{ ...normalText, height: '42px', alignSelf: 'end' }}>
            {loading ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: '13px',
                    height: '13px',
                    border: '2px solid rgba(255,255,255,0.45)',
                    borderTopColor: '#ffffff',
                    borderRadius: '50%',
                    animation: 'rsl-backtest-spin 0.8s linear infinite',
                  }}
                />
                Berechne ...
              </span>
            ) : isParameterComparisonMode ? 'Vergleich laden' : 'Backtest starten'}
          </Btn>
        </div>
        {isAdmin && isStrategySelectionMode && (
          <div style={{ marginTop: '14px', borderTop: `1px solid ${COLORS.border}`, paddingTop: '12px' }}>
            {isHistoricalStrategyMode && (
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '9px', cursor: loading ? 'default' : 'pointer', ...normalText, fontSize: '13px', color: COLORS.text }}>
                  <input
                    type="checkbox"
                    checked={dynamicSizing}
                    disabled={loading}
                    onChange={(event) => setDynamicSizing(event.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: COLORS.cyan }}
                  />
                  <span>Dynamisches Sizing bei Neueinstieg</span>
                  <span style={{ color: COLORS.muted, fontSize: '12px' }}>
                    (aus: fixer Betrag je Trade · an: Depotwert / Anzahl Titel, begrenzt durch Cash)
                  </span>
                </label>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
              <div />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Btn color="ghost" onClick={() => setSelectedStrategyCodes(defaultVisibleStrategyCodes)} disabled={loading} style={{ ...normalText, padding: '7px 10px', fontSize: '12px' }}>
                  Alle
                </Btn>
                <Btn color="ghost" onClick={() => setSelectedStrategyCodes([])} disabled={loading} style={{ ...normalText, padding: '7px 10px', fontSize: '12px' }}>
                  Keine
                </Btn>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '8px' }}>
              {visibleStrategyVariants.map((variant) => {
                const checked = selectedStrategyCodes.includes(variant.code);
                const accent = variant.universe === 'sector_etfs' ? '#f59e0b' : COLORS.cyan;
                return (
                  <label
                    key={variant.code}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '9px',
                      border: `1px solid ${checked ? accent : COLORS.border}`,
                      borderRadius: '8px',
                      padding: '9px 10px',
                      background: checked ? (variant.universe === 'sector_etfs' ? '#f59e0b18' : '#00d9ff14') : '#ffffff05',
                      color: checked ? COLORS.text : COLORS.muted,
                      fontSize: '12px',
                      fontWeight: 800,
                      cursor: loading ? 'default' : 'pointer',
                      ...normalText,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={loading}
                      onChange={() => toggleStrategyCode(variant.code)}
                      style={{ width: '16px', height: '16px', accentColor: accent }}
                    />
                    <span style={{ color: accent }}>{variant.code}</span>
                    <span>{variant.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
        {isParameterComparisonMode && (
          <div style={{ marginTop: '14px', borderTop: `1px solid ${COLORS.border}`, paddingTop: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
              <div>
                <div title={PARAMETER_COMPARISON_TOOLTIPS.positions} style={{ color: COLORS.muted, fontSize: '12px', fontWeight: 800, marginBottom: '8px', cursor: 'help' }}>Anzahl Positionen</div>
                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  <Btn color="ghost" size="sm" onClick={() => setParameterPositions(PARAMETER_COMPARISON_POSITIONS)} disabled={loading}>Alle</Btn>
                  <Btn color="ghost" size="sm" onClick={() => setParameterPositions([])} disabled={loading}>Keine</Btn>
                  {PARAMETER_COMPARISON_POSITIONS.map((value) => {
                    const checked = parameterPositions.includes(value);
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => toggleNumberSelection(value, setParameterPositions)}
                        disabled={loading}
                        style={{
                          border: `1px solid ${checked ? COLORS.cyan : COLORS.border}`,
                          background: checked ? '#00d9ff1a' : '#ffffff05',
                          color: checked ? COLORS.text : COLORS.muted,
                          borderRadius: '8px',
                          padding: '7px 10px',
                          fontWeight: 900,
                          cursor: loading ? 'default' : 'pointer',
                        }}
                      >
                        {value}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div title={PARAMETER_COMPARISON_TOOLTIPS.exitCriteria} style={{ color: COLORS.muted, fontSize: '12px', fontWeight: 800, marginBottom: '8px', cursor: 'help' }}>Ausstiegskriterium</div>
                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  {PARAMETER_COMPARISON_EXIT_CRITERIA.map((option) => {
                    const checked = parameterExitCriteria.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleStringSelection(option.value, setParameterExitCriteria)}
                        disabled={loading}
                        style={{
                          border: `1px solid ${checked ? COLORS.cyan : COLORS.border}`,
                          background: checked ? '#00d9ff1a' : '#ffffff05',
                          color: checked ? COLORS.text : COLORS.muted,
                          borderRadius: '8px',
                          padding: '7px 10px',
                          fontWeight: 900,
                          cursor: loading ? 'default' : 'pointer',
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div title={PARAMETER_COMPARISON_TOOLTIPS.exitPercents} style={{ color: COLORS.muted, fontSize: '12px', fontWeight: 800, marginBottom: '8px', cursor: 'help' }}>Top % im Index</div>
                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  <Btn color="ghost" size="sm" onClick={() => setParameterExitPercents(PARAMETER_COMPARISON_EXIT_PERCENTS)} disabled={loading}>Alle</Btn>
                  <Btn color="ghost" size="sm" onClick={() => setParameterExitPercents([])} disabled={loading || !parameterExitCriteria.includes('k_exit')}>Keine</Btn>
                  {PARAMETER_COMPARISON_EXIT_PERCENTS.map((value) => {
                    const checked = parameterExitPercents.includes(value);
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => toggleNumberSelection(value, setParameterExitPercents)}
                        disabled={loading || !parameterExitCriteria.includes('k_exit')}
                        style={{
                          border: `1px solid ${checked ? COLORS.cyan : COLORS.border}`,
                          background: checked ? '#00d9ff1a' : '#ffffff05',
                          color: checked ? COLORS.text : COLORS.muted,
                          opacity: parameterExitCriteria.includes('k_exit') ? 1 : 0.45,
                          borderRadius: '8px',
                          padding: '7px 10px',
                          fontWeight: 900,
                          cursor: loading ? 'default' : 'pointer',
                        }}
                      >
                        {value}%
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '14px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ color: COLORS.muted, fontSize: '12px', fontWeight: 800, marginBottom: '8px' }}>Favoriten</div>
                <Btn color="accent" size="sm" onClick={applyModelPortfolioParameterPreset} disabled={loading}>
                  Varianten im RSL-Scanner
                </Btn>
              </div>
              <div style={{ color: COLORS.muted, fontSize: '12px', fontWeight: 800, textAlign: 'right', ...normalText }}>
                Basisinvest: 10.000 EUR/USD
              </div>
            </div>
          </div>
        )}
        {error && <div style={{ marginTop: '12px', color: COLORS.red, fontSize: '13px' }}>⚠ {error}</div>}
        {loading && !isParameterComparisonMode && (
          <div style={{ marginTop: '12px', color: COLORS.muted, fontSize: '13px' }}>
            Beim ersten Lauf werden historische Wochenkurse geladen und gespeichert. Das kann je nach Markt etwas dauern.
          </div>
        )}
        {!isAdminSignalMode && !isParameterComparisonMode && form.period === 'CUSTOM' && (
          <div style={{ marginTop: '10px', color: COLORS.muted, fontSize: '12px' }}>
            Das Startdatum wird automatisch auf den Montag der gewählten Woche gesetzt. Beim Enddatum wird die letzte verfügbare Wochenkerze bis zu diesem Datum verwendet.
          </div>
        )}
        {hdaxHistoryNote && (
          <div style={{ marginTop: '10px', color: COLORS.yellow || '#f59e0b', fontSize: '12px', fontWeight: 700 }}>
            {hdaxHistoryNote}
          </div>
        )}
        {v11HistoryNote && (
          <div style={{ marginTop: '10px', color: COLORS.yellow || '#f59e0b', fontSize: '12px', fontWeight: 700 }}>
            {v11HistoryNote}
          </div>
        )}
        {!isAdminSignalMode && !isParameterComparisonMode && (
          <div style={{ marginTop: '10px', color: COLORS.muted, fontSize: '12px' }}>
            Branche = maximale Anzahl gleichzeitig offener Titel aus derselben Branche. 0 bedeutet keine Begrenzung.
            {isHistoricalStrategyMode ? ' Top % im Index steuert, ab welchem Rankingverlust K1-K4 und EK1-EK4 verkaufen.' : ''}
          </div>
        )}
      </Card>

      {result && isParameterComparisonResult && (
        <>
          <Card style={{ marginBottom: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
              <div>
                <strong>{resultMarketLabel} · Parameter-Vergleich</strong>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
              {[
                ['Varianten', summary.variant_count || comparisonVariants.length],
                ['Bester Endwert', `${formatParameterMoney(summary.best_end_value, currency)} ${currency}`],
                ['Beste Gesamt %', formatPercentRounded(summary.best_total_pl_percent), summary.best_total_pl_percent],
                ['Höchster Gewinn', `${parameterComparisonDisplayCode(summary.highest_profit_variant) || '—'} · ${formatParameterMoney(summary.highest_profit_end_value, currency)} ${currency}`, summary.highest_profit_total_pl_percent],
                ['Geringster Drawdown', `${parameterComparisonDisplayCode(summary.lowest_drawdown_variant) || '—'} · ${formatPercent(summary.lowest_drawdown)}`],
                ['Variante im RSL Musterdepot', summary.model_portfolio_variant ? `${parameterComparisonDisplayCode(summary.model_portfolio_variant)} · ${formatParameterMoney(summary.model_portfolio_end_value, currency)} ${currency}` : 'Keine passende Variante', summary.model_portfolio_total_pl_percent],
                [`Benchmark ${benchmark.label || 'Index'}`, Number.isFinite(benchmarkEndValue) ? `${formatParameterMoney(benchmarkEndValue, currency)} ${currency} · ${formatPercentRounded(benchmarkTotalPercent)}` : '—', benchmarkTotalPercent],
              ].map(([label, value, signed]) => (
                <div key={label} style={{ border: `1px solid ${COLORS.border}`, borderRadius: '14px', padding: '12px', background: '#ffffff05' }}>
                  <div style={{ color: COLORS.muted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
                  <div style={{ marginTop: '6px', fontWeight: 800, color: signed === undefined ? COLORS.text : signedColor(signed, COLORS) }}>{value}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card style={{ marginBottom: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <strong>Parameter-Kurven</strong>
                <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '4px' }}>
                  Jede Linie steht für eine Kombination aus Positionsanzahl und Ausstieg. Die Kurve basiert auf Daten der Vergangenheit, zukünftige Ergebnisse können abweichen!
                </div>
              </div>
              <div style={{ display: 'inline-flex', background: '#ffffff08', border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                {[
                  ['linear', 'Absolut'],
                  ['log', 'Logarithmisch'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setComparisonChartScale(value)}
                    style={{
                      border: 'none',
                      borderRight: value === 'linear' ? `1px solid ${COLORS.border}` : 'none',
                      background: comparisonChartScale === value ? COLORS.cyan : 'transparent',
                      color: comparisonChartScale === value ? '#00131a' : COLORS.text,
                      padding: '7px 10px',
                      fontSize: '12px',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <StrategyComparisonChart
              variants={parameterComparisonChartVariants}
              indexCurve={result.index_curve}
              benchmark={benchmark}
              COLORS={COLORS}
              currency={currency}
              themeMode={themeMode}
              scaleMode={comparisonChartScale}
              showLegend={false}
              maxTooltipItems={10}
              formatValue={(value) => formatParameterMoney(value, currency)}
            />
          </Card>

          <Card>
            <div style={{ marginBottom: '12px' }}>
              <strong>Kennzahlen</strong>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '920px' }}>
                <thead>
                  <tr>
                    {parameterColumns.map((column) => (
                      <th key={column.key} style={th}>
                        <button
                          type="button"
                          onClick={() => toggleParameterSort(column.key)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'inherit',
                            font: 'inherit',
                            cursor: 'pointer',
                            padding: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                          title={`${column.label} sortieren`}
                        >
                          <span>{column.label}</span>
                          <span style={{ color: parameterSort.key === column.key ? COLORS.text : COLORS.muted, fontSize: '11px' }}>
                            {parameterSort.key === column.key ? (parameterSort.direction === 'asc' ? '▲' : '▼') : '↕'}
                          </span>
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parameterBenchmarkSummary && (
                    <tr>
                      <td style={td}><strong style={{ color: COLORS.cyan }}>Benchmark</strong></td>
                      <td style={td}>—</td>
                      <td style={td}>Buy & Hold</td>
                      <td style={td}>{formatParameterMoney(parameterBenchmarkSummary.end_value, currency)} {currency}</td>
                      <td style={{ ...td, color: signedColor(parameterBenchmarkSummary.total_pl_percent, COLORS), fontWeight: 800 }}>{formatPercentRounded(parameterBenchmarkSummary.total_pl_percent)}</td>
                      <td style={{ ...td, color: signedColor(parameterBenchmarkSummary.ror, COLORS), fontWeight: 800 }}>{formatPercent(parameterBenchmarkSummary.ror)}</td>
                      <td style={{ ...td, color: signedColor(parameterBenchmarkSummary.max_drawdown, COLORS), fontWeight: 800 }}>{formatPercent(parameterBenchmarkSummary.max_drawdown)}</td>
                      <td style={td}>{formatMoney(parameterBenchmarkSummary.mar)}</td>
                      <td style={td}>—</td>
                      <td style={td}>—</td>
                      <td style={td}>—</td>
                    </tr>
                  )}
                  {sortedParameterComparisonVariants.map((variant) => {
                    const variantSummary = variant.summary || {};
                    const exitLabel = variant.exit_criterion === 'index_rsl'
                      ? 'Index-RSL'
                      : `Top ${variant.exit_percent_filter}% im Index`;
                    return (
                      <tr key={variant.code}>
                        <td style={td}><strong style={{ color: COLORS.cyan }}>{parameterComparisonDisplayCode(variant.code)}</strong></td>
                        <td style={td}>{variant.positions_count}</td>
                        <td style={td}>{exitLabel}</td>
                        <td style={td}>{formatParameterMoney(variantSummary.end_value, currency)} {currency}</td>
                        <td style={{ ...td, color: signedColor(variantSummary.total_pl_percent, COLORS), fontWeight: 800 }}>{formatPercentRounded(variantSummary.total_pl_percent)}</td>
                        <td style={{ ...td, color: signedColor(variantSummary.ror ?? variantSummary.cagr, COLORS), fontWeight: 800 }}>{formatPercent(variantSummary.ror ?? variantSummary.cagr)}</td>
                        <td style={{ ...td, color: signedColor(variantSummary.max_drawdown, COLORS), fontWeight: 800 }}>{formatPercent(variantSummary.max_drawdown)}</td>
                        <td style={td}>{formatMoney(variantSummary.mar)}</td>
                        <td style={td}>{formatMoney(variantSummary.sharpe)}</td>
                        <td style={td}>{formatMoney(variantSummary.profit_factor)}</td>
                        <td style={td}>{variantSummary.closed_trades || 0}</td>
                      </tr>
                    );
                  })}
                  {comparisonVariants.length === 0 && (
                    <tr>
                      <td colSpan={11} style={{ ...td, color: COLORS.muted }}>Keine Varianten gefunden.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {result && isComparisonResult && (
        <>
          <Card style={{ marginBottom: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
              <div>
                <strong>{resultMarketLabel} · {result.period}</strong>
                <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '4px' }}>
                  {formatDate(result.start_date)} bis {formatDate(result.end_date)} · Währung {currency}
                </div>
              </div>
              <Badge color="ghost">
                {summary.variant_count || comparisonVariants.length} Varianten
              </Badge>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px' }}>
              {[
                ['Startkapital Aktien', `${formatMoney(summary.stock_start_capital ?? summary.start_capital)} ${currency}`],
                ...(hasSectorComparisonVariant ? [['Startkapital ETFs', `${formatMoney(summary.sector_start_capital)} ${currency}`]] : []),
                ['Beste Variante', `${summary.best_variant || '—'} ${summary.best_variant_label || ''}`.trim()],
                ['Bester Endwert', `${formatMoney(summary.best_end_value)} ${currency}`],
                ['Beste Gesamt %', formatPercent(summary.best_total_pl_percent), summary.best_total_pl_percent],
                [`${benchmark.label || 'Index'} Endwert`, `${formatMoney(summary.index_end_value)} ${currency}`],
                [`${benchmark.label || 'Index'} %`, formatPercent(summary.index_pl_percent), summary.index_pl_percent],
              ].map(([label, value, signed]) => (
                <div key={label} style={{ border: `1px solid ${COLORS.border}`, borderRadius: '14px', padding: '12px', background: '#ffffff05' }}>
                  <div style={{ color: COLORS.muted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
                  <div style={{ marginTop: '6px', fontWeight: 800, color: signed === undefined ? COLORS.text : signedColor(signed, COLORS) }}>{value}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '14px', color: COLORS.muted, fontSize: '12px', lineHeight: 1.7 }}>
              {executionMode === 'historical_strategy_comparison_v10'
                ? 'v1.0 nutzt die historische Datenbasis ab 1990 und schreibt sie bis gestern fort.'
                : 'v0.9 nutzt den Norgate-Snapshot von 1990 bis 18.05.2026.'} Cash bedeutet: 3-Wochen-Durchschnitt des Index-RSL unter 1,00 verkauft zum nächsten handelbaren Open und pausiert neue Käufe bis zur Erholung auf mindestens 1,00.
            </div>
          </Card>

          <Card style={{ marginBottom: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <strong>Strategie-Vergleich</strong>
                <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '4px' }}>
                  Alle Varianten starten mit gleichem Kapital und gleicher Positionsanzahl. Die Kurven zeigen den simulierten Depotwert.
                </div>
              </div>
              <div style={{ display: 'inline-flex', background: '#ffffff08', border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                {[
                  ['linear', 'Absolut'],
                  ['log', 'Logarithmisch'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setComparisonChartScale(value)}
                    style={{
                      border: 'none',
                      borderRight: value === 'linear' ? `1px solid ${COLORS.border}` : 'none',
                      background: comparisonChartScale === value ? COLORS.cyan : 'transparent',
                      color: comparisonChartScale === value ? '#00131a' : COLORS.text,
                      padding: '7px 10px',
                      fontSize: '12px',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <StrategyComparisonChart variants={comparisonVariants} indexCurve={result.index_curve} benchmark={benchmark} COLORS={COLORS} currency={currency} themeMode={themeMode} scaleMode={comparisonChartScale} />
          </Card>

          <Card>
            <div style={{ marginBottom: '12px' }}>
              <strong>Ergebnistabelle</strong>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '980px' }}>
                <thead>
                  <tr>
                    {['Variante', 'Regel', 'Startkapital', 'Endkapital', 'Gesamt %', 'CAGR', 'Max DD', 'Trefferquote', 'Trades', 'Zeit im Markt', 'Beste Woche', 'Schlechteste Woche', 'CSV'].map((label) => (
                      <th key={label} style={th}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonVariants.map((variant) => {
                    const variantSummary = variant.summary || {};
                    const baseRule = variant.exit_mode === 'rsl_floor'
                      ? 'RSL < 1'
                      : variant.exit_mode === 'classic'
                        ? `Top ${classicExitRankPct}% / RSL < 1`
                        : 'RSL < Index / RSL < 1';
                    const rule = `${baseRule}${variant.use_ma_filter ? ' + MA 5/13' : ''}${variant.use_cash_filter ? ' + Cash' : ''}`;
                    return (
                      <tr key={variant.code}>
                        <td style={td}><strong style={{ color: COLORS.cyan }}>{variant.code}</strong></td>
                        <td style={td}>{rule}</td>
                        <td style={td}>{formatMoney(variantSummary.start_capital)} {currency}</td>
                        <td style={td}>{formatMoney(variantSummary.end_value)} {currency}</td>
                        <td style={{ ...td, color: signedColor(variantSummary.total_pl_percent, COLORS), fontWeight: 800 }}>{formatPercent(variantSummary.total_pl_percent)}</td>
                        <td style={{ ...td, color: signedColor(variantSummary.cagr, COLORS), fontWeight: 800 }}>{formatPercent(variantSummary.cagr)}</td>
                        <td style={{ ...td, color: signedColor(variantSummary.max_drawdown, COLORS), fontWeight: 800 }}>{formatPercent(variantSummary.max_drawdown)}</td>
                        <td style={td}>{formatPercent(variantSummary.win_rate)}</td>
                        <td style={td}>{variantSummary.closed_trades || 0}</td>
                        <td style={td}>{formatPercent(variantSummary.time_in_market_percent)}</td>
                        <td style={{ ...td, color: signedColor(variantSummary.best_week_percent, COLORS), fontWeight: 800 }}>{formatPercent(variantSummary.best_week_percent)}</td>
                        <td style={{ ...td, color: signedColor(variantSummary.worst_week_percent, COLORS), fontWeight: 800 }}>{formatPercent(variantSummary.worst_week_percent)}</td>
                        <td style={td}>
                          <Btn color="green" size="sm" onClick={() => exportVariantCsv(variant)} style={{ padding: '6px 9px', fontSize: '11px' }}>
                            CSV
                          </Btn>
                        </td>
                      </tr>
                    );
                  })}
                  {comparisonVariants.length === 0 && (
                    <tr>
                      <td colSpan={13} style={{ ...td, color: COLORS.muted }}>Keine Vergleichsdaten vorhanden.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {result && !isComparisonResult && !isParameterComparisonResult && (
        <>
          <Card style={{ marginBottom: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
              <div>
                <strong>{resultMarketLabel} · {result.period}</strong>
                <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '4px' }}>
                  {formatDate(result.start_date)} bis {formatDate(result.end_date)} · Währung {currency}
                </div>
              </div>
              <Badge color="ghost">
                Cache: +{result.cache?.inserted || 0} neu / {result.cache?.updated || 0} aktualisiert
              </Badge>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
              {[
                ['Startkapital', `${formatMoney(summary.start_capital)} ${currency}`],
                ['Endwert', `${formatMoney(summary.end_value)} ${currency}`],
                ['Gesamt P/L', `${formatMoney(summary.total_pl)} ${currency}`, summary.total_pl],
                ['Gesamt %', formatPercent(summary.total_pl_percent), summary.total_pl_percent],
                ['Realisiert', `${formatMoney(summary.realized_pl)} ${currency}`, summary.realized_pl],
                ['Buch P/L', `${formatMoney(summary.book_pl)} ${currency}`, summary.book_pl],
                ['Trades', `${summary.closed_trades || 0} geschlossen / ${summary.open_trades || 0} offen`],
                ['Gewinner', `${summary.winning_trades || 0} · ${formatPercent(summary.win_rate)}`],
                ['Gebühren', `${formatMoney(summary.total_fees)} ${currency}`],
                ['Branchenlimit', `${result.parameters?.max_per_industry || 0} je Branche`],
                ['Universum', result.parameters?.universe_mode === 'historical' ? 'Historisch' : 'Aktuell'],
                ['Max Drawdown', formatPercent(summary.max_drawdown), summary.max_drawdown],
                [`${benchmark.label || 'Index'} Endwert`, `${formatMoney(summary.index_end_value)} ${currency}`],
                [`${benchmark.label || 'Index'} %`, formatPercent(summary.index_pl_percent), summary.index_pl_percent],
                ['Alpha vs Index', `${formatMoney(summary.alpha_vs_index)} ${currency}`, summary.alpha_vs_index],
                ['Alpha %', formatPercent(summary.alpha_vs_index_percent), summary.alpha_vs_index_percent],
              ].map(([label, value, signed]) => (
                <div key={label} style={{ border: `1px solid ${COLORS.border}`, borderRadius: '14px', padding: '12px', background: '#ffffff05' }}>
                  <div style={{ color: COLORS.muted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
                  <div style={{ marginTop: '6px', fontWeight: 800, color: signed === undefined ? COLORS.text : signedColor(signed, COLORS) }}>{value}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '14px', color: COLORS.muted, fontSize: '12px', lineHeight: 1.7 }}>
              Backtest-Simulationen basieren auf historischen Daten, die „wie besehen“ bereitgestellt werden, ohne Gewähr für Richtigkeit, Vollständigkeit oder Fehlerfreiheit; sie dienen ausschließlich Informationszwecken und stellen weder eine Anlageberatung noch eine Garantie für zukünftige Performance dar.
            </div>
          </Card>

          <Card style={{ marginBottom: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <strong>Equity-Chart</strong>
                <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '4px' }}>
                  RSL Strategie gegen {benchmark.label || 'Index'} Buy & Hold bei gleichem Startkapital. Die Index-Zahl ist ein simulierter Depotwert, nicht der Punktestand.
                </div>
              </div>
              <div style={{ display: 'inline-flex', background: '#ffffff08', border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                {[
                  ['linear', 'Absolut'],
                  ['log', 'Logarithmisch'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setComparisonChartScale(value)}
                    style={{
                      border: 'none',
                      borderRight: value === 'linear' ? `1px solid ${COLORS.border}` : 'none',
                      background: comparisonChartScale === value ? COLORS.cyan : 'transparent',
                      color: comparisonChartScale === value ? '#00131a' : COLORS.text,
                      padding: '7px 10px',
                      fontSize: '12px',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <EquityComparisonChart equityCurve={equityCurve} indexCurve={indexCurve} benchmark={benchmark} COLORS={COLORS} currency={currency} themeMode={themeMode} scaleMode={comparisonChartScale} />
          </Card>

          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <strong>Einzelne Trades</strong>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ color: COLORS.muted, fontSize: '12px' }}>{trades.length} Einträge</span>
                <Btn onClick={exportBacktestCsv} color="green" size="sm">CSV Export</Btn>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '980px' }}>
                <thead>
                  <tr>
                    {tradeColumns.map((column) => (
                      <th key={column.key} style={th}>
                        <button
                          type="button"
                          onClick={() => toggleTradeSort(column.key)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'inherit',
                            font: 'inherit',
                            cursor: 'pointer',
                            padding: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                          title={`${column.label} sortieren`}
                        >
                          <span>{column.label}</span>
                          <span style={{ color: tradeSort.key === column.key ? COLORS.text : COLORS.muted, fontSize: '11px' }}>
                            {tradeSort.key === column.key ? (tradeSort.direction === 'asc' ? '▲' : '▼') : '↕'}
                          </span>
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedTrades.map((trade, index) => (
                    <tr key={`${trade.symbol}-${trade.entry_date}-${trade.exit_date || 'open'}-${index}`}>
                      <td style={td}><strong style={{ color: COLORS.cyan }}>{trade.symbol}</strong></td>
                      <td style={td}>{trade.industry || '—'}</td>
                      <td style={td}>{formatDate(trade.entry_date)}</td>
                      <td style={td}>{formatMoney(trade.entry_price)} {currency}</td>
                      <td style={td}>#{trade.entry_rank || '—'}</td>
                      <td style={td}>{trade.entry_rsl ?? '—'}</td>
                      <td style={td}>{trade.exit_date ? formatDate(trade.exit_date) : <Badge color="green">Offen</Badge>}</td>
                      <td style={td}>{trade.exit_price ? `${formatMoney(trade.exit_price)} ${currency}` : '—'}</td>
                      <td style={td}>{trade.days ?? '—'}d</td>
                      <td style={{ ...td, color: signedColor(trade.realized_pl, COLORS), fontWeight: 800 }}>{formatMoney(trade.realized_pl)} {currency}</td>
                      <td style={{ ...td, color: signedColor(trade.realized_pl_percent, COLORS), fontWeight: 800 }}>{formatPercent(trade.realized_pl_percent)}</td>
                      <td style={td}>{trade.exit_reason || '—'}</td>
                    </tr>
                  ))}
                  {sortedTrades.length === 0 && (
                    <tr>
                      <td colSpan={12} style={{ ...td, color: COLORS.muted }}>Keine Trades im Zeitraum.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
